'use client';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { OverlayPanel } from 'primereact/overlaypanel';

// ── Feedback options ───────────────────────────────────────────────────────────

const OPTIONS = [
    {
        id: 'delivery_too_late',
        icon: 'pi-clock',
        label: 'Delivery too late',
        desc: 'Expected delivery time is too long',
        color: '#f97316',
        bg: '#fff7ed',
        border: '#fed7aa',
    },
    {
        id: 'not_serviceable',
        icon: 'pi-map-marker',
        label: 'Shipping service not available at my location',
        desc: 'Service unavailable for my pincode',
        color: '#ef4444',
        bg: '#fef2f2',
        border: '#fecaca',
    },
    {
        id: 'poor_service',
        icon: 'pi-thumbs-down',
        label: 'Not the best service',
        desc: 'Delivery experience needs improvement',
        color: '#8b5cf6',
        bg: '#f5f3ff',
        border: '#ddd6fe',
    },
];

// ── Source metadata helpers ────────────────────────────────────────────────────

const getBrowserName = (ua = '') => {
    if (ua.includes('Firefox'))        return 'Firefox';
    if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
    if (ua.includes('OPR') || ua.includes('Opera')) return 'Opera';
    if (ua.includes('Edg'))            return 'Edge';
    if (ua.includes('Chrome'))         return 'Chrome';
    if (ua.includes('Safari'))         return 'Safari';
    return 'Unknown';
};

const getDeviceType = (w = 0) => {
    if (w < 768)  return 'mobile';
    if (w < 1024) return 'tablet';
    return 'web';
};

const collectSourceInfo = async (sourceName) => {
    const ua     = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const width  = typeof window   !== 'undefined' ? window.innerWidth  : 0;
    const height = typeof window   !== 'undefined' ? window.innerHeight : 0;
    const info = {
        source:      sourceName || 'delivery_check',
        page:        typeof window   !== 'undefined' ? window.location.pathname + window.location.search : '',
        pageTitle:   typeof document !== 'undefined' ? document.title : '',
        browser:     getBrowserName(ua),
        deviceType:  getDeviceType(width),
        screenWidth: width,
        screenHeight: height,
        language:    typeof navigator !== 'undefined' ? navigator.language : '',
        referrer:    typeof document !== 'undefined' ? document.referrer  : '',
        userAgent:   ua,
        ip:          null,
    };
    try {
        const res  = await fetch('https://api.ipify.org?format=json', { signal: AbortSignal.timeout(3000) });
        const json = await res.json();
        info.ip = json.ip;
    } catch (_) {}
    return info;
};

// ── Inner feedback UI ──────────────────────────────────────────────────────────

export const DeliveryFeedbackContent = ({ pincode, productId, source, onClose }) => {
    const [selected,   setSelected]   = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [done,       setDone]       = useState(false);

    const toggle = (id) =>
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );

    const handleSubmit = async () => {
        if (!selected.length) return;
        setSubmitting(true);
        try {
            const sourceInfo = await collectSourceInfo(source);
            await fetch('/api/delivery/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pincode,
                    reasons: selected,
                    ...(productId && { productId }),
                    ...sourceInfo,
                }),
            });
        } catch (_) {}
        setSubmitting(false);
        setDone(true);
    };

    if (done) {
        return (
            <div style={{ padding: '1.75rem 1.25rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10b981, #34d399)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 20px rgba(16,185,129,0.35)',
                }}>
                    <i className="pi pi-check" style={{ fontSize: '1.4rem', color: '#fff' }} />
                </div>
                <div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>Thank you for your feedback!</p>
                    <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.6, maxWidth: 260, marginInline: 'auto' }}>
                        Based on your input, we will process accordingly and faster try delivery to your location.
                    </p>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        marginTop: 4, padding: '8px 22px', borderRadius: 10,
                        background: 'var(--primary-color)', color: '#fff',
                        border: 'none', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                    }}
                >
                    Done
                </button>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>

            {/* Header */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.85rem 1rem 0.75rem',
                background: '#f8fafc', borderBottom: '1px solid #e5e7eb',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <i className="pi pi-comment" style={{ color: '#fff', fontSize: '0.82rem' }} />
                    </div>
                    <div>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: '0.88rem', color: '#111827', lineHeight: 1.2 }}>
                            Delivery Feedback
                        </p>
                        {pincode && (
                            <p style={{ margin: 0, fontSize: '0.66rem', color: '#6b7280', marginTop: 1 }}>
                                Pincode {pincode}
                            </p>
                        )}
                    </div>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        width: 28, height: 28, borderRadius: '50%',
                        border: '1px solid #e5e7eb', background: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: '#6b7280', flexShrink: 0,
                    }}
                >
                    <i className="pi pi-times" style={{ fontSize: '0.65rem' }} />
                </button>
            </div>

            {/* Body */}
            <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <p style={{ margin: '0 0 0.35rem', fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>
                    What went wrong? Select all that apply.
                </p>

                {OPTIONS.map((opt) => {
                    const isSelected = selected.includes(opt.id);
                    return (
                        <button
                            key={opt.id}
                            type="button"
                            onClick={() => toggle(opt.id)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '0.75rem',
                                padding: '0.7rem 0.85rem',
                                borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                                background: isSelected ? opt.bg : '#fff',
                                border: `1.5px solid ${isSelected ? opt.color : '#e5e7eb'}`,
                                transition: 'all 0.15s',
                                boxShadow: isSelected ? `0 0 0 3px ${opt.color}18` : 'none',
                                width: '100%',
                            }}
                        >
                            <div style={{
                                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                background: isSelected ? opt.color : '#f3f4f6',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background 0.15s',
                            }}>
                                <i
                                    className={`pi ${opt.icon}`}
                                    style={{ fontSize: '0.88rem', color: isSelected ? '#fff' : '#9ca3af', transition: 'color 0.15s' }}
                                />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{
                                    margin: 0, fontWeight: 700, fontSize: '0.82rem',
                                    color: isSelected ? opt.color : '#111827',
                                    transition: 'color 0.15s',
                                }}>
                                    {opt.label}
                                </p>
                                <p style={{ margin: 0, fontSize: '0.68rem', color: '#9ca3af', marginTop: 2 }}>
                                    {opt.desc}
                                </p>
                            </div>
                            <div style={{
                                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                                background: isSelected ? opt.color : 'transparent',
                                border: `2px solid ${isSelected ? opt.color : '#d1d5db'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.15s',
                            }}>
                                {isSelected && <i className="pi pi-check" style={{ fontSize: '0.55rem', color: '#fff' }} />}
                            </div>
                        </button>
                    );
                })}

                {/* Info note */}
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                    padding: '0.6rem 0.75rem', borderRadius: 8,
                    background: '#eff6ff', border: '1px solid #bfdbfe', marginTop: 2,
                }}>
                    <i className="pi pi-info-circle flex-shrink-0" style={{ fontSize: '0.72rem', color: '#3b82f6', marginTop: 2 }} />
                    <p style={{ margin: 0, fontSize: '0.7rem', color: '#1d4ed8', lineHeight: 1.55 }}>
                        Based on your input, we will process accordingly and faster try delivery to your location.
                    </p>
                </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '0 1rem 1rem', display: 'flex', gap: '0.5rem' }}>
                <button
                    type="button"
                    onClick={onClose}
                    style={{
                        flex: 1, padding: '0.65rem', borderRadius: 10,
                        background: '#f3f4f6', border: '1px solid #e5e7eb',
                        color: '#374151', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
                    }}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!selected.length || submitting}
                    style={{
                        flex: 2, padding: '0.65rem', borderRadius: 10, border: 'none',
                        background: selected.length ? 'var(--primary-color)' : '#e5e7eb',
                        color: selected.length ? '#fff' : '#9ca3af',
                        fontWeight: 700, fontSize: '0.82rem',
                        cursor: selected.length && !submitting ? 'pointer' : 'not-allowed',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        transition: 'all 0.15s',
                    }}
                >
                    {submitting ? (
                        <><i className="pi pi-spin pi-spinner" style={{ fontSize: '0.8rem' }} /> Submitting…</>
                    ) : (
                        <><i className="pi pi-send" style={{ fontSize: '0.78rem' }} /> Submit Feedback</>
                    )}
                </button>
            </div>
        </div>
    );
};

// ── Bottom sheet (mobile + tablet < 1024px) ────────────────────────────────────

const FeedbackBottomSheet = ({ pincode, productId, source, onClose }) => {
    const [sheetIn, setSheetIn] = useState(false);

    useEffect(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => setSheetIn(true)));
    }, []);

    const handleClose = () => {
        setSheetIn(false);
        setTimeout(onClose, 320);
    };

    return createPortal(
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: sheetIn ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0)',
                transition: 'background 0.32s',
                backdropFilter: sheetIn ? 'blur(2px)' : 'none',
            }}
            onClick={handleClose}
        >
            <div
                style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: '#fff',
                    borderRadius: '20px 20px 0 0',
                    boxShadow: '0 -8px 40px rgba(0,0,0,0.15)',
                    transform: sheetIn ? 'translateY(0)' : 'translateY(100%)',
                    transition: 'transform 0.32s cubic-bezier(0.32,0.72,0,1)',
                    maxHeight: '88vh', overflowY: 'auto',
                    paddingBottom: 'env(safe-area-inset-bottom, 0)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 2 }}>
                    <div style={{ width: 36, height: 4, borderRadius: 2, background: '#d1d5db' }} />
                </div>
                <DeliveryFeedbackContent
                    pincode={pincode}
                    productId={productId}
                    source={source}
                    onClose={handleClose}
                />
            </div>
        </div>,
        document.body
    );
};

// ── Public trigger — help icon + overlay (desktop) / sheet (mobile+tablet) ─────
// variant='header'  → white circle + white icon  (for use on primary-colored headers)
// variant='inline'  → teal circle + teal icon    (for use on light/white backgrounds)

export const DeliveryFeedbackTrigger = ({ pincode, productId, source, variant = 'header' }) => {
    const opRef      = useRef(null);
    const [showSheet, setShowSheet] = useState(false);
    const [mounted,   setMounted]   = useState(false);
    const [isCompact, setIsCompact] = useState(false);

    useEffect(() => {
        setMounted(true);
        const update = () => setIsCompact(window.innerWidth < 1024);
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    const handleClick = (e) => {
        if (isCompact) {
            setShowSheet(true);
        } else {
            opRef.current?.toggle(e);
        }
    };

    if (!mounted) return null;

    const isHeader = variant === 'header';
    const isBadge  = variant === 'badge';

    // ── Badge variant — full pill with text ───────────────────────────────────
    if (isBadge) {
        return (
            <>
                <button
                    type="button"
                    onClick={handleClick}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '0.45rem',
                        width: '100%', padding: '0.5rem 0.75rem',
                        background: '#fffbeb', border: '1px solid #fde68a',
                        borderRadius: '8px', cursor: 'pointer', textAlign: 'left',
                        transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#fef3c7')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#fffbeb')}
                >
                    <div style={{
                        width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                        background: '#f59e0b',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <i className="pi pi-comment" style={{ color: '#fff', fontSize: '0.65rem' }} />
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600, flex: 1 }}>
                        Something wrong? Give delivery feedback
                    </span>
                    <i className="pi pi-chevron-right" style={{ fontSize: '0.6rem', color: '#d97706', flexShrink: 0 }} />
                </button>

                {!isCompact && (
                    <OverlayPanel
                        ref={opRef}
                        className="p-0"
                        style={{
                            width: 360, maxWidth: '95vw',
                            borderRadius: 16, overflow: 'hidden',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
                            border: '1px solid #e5e7eb',
                        }}
                    >
                        <DeliveryFeedbackContent
                            pincode={pincode}
                            productId={productId}
                            source={source}
                            onClose={() => opRef.current?.hide()}
                        />
                    </OverlayPanel>
                )}

                {showSheet && (
                    <FeedbackBottomSheet
                        pincode={pincode}
                        productId={productId}
                        source={source}
                        onClose={() => setShowSheet(false)}
                    />
                )}
            </>
        );
    }

    // ── Icon-only variants (header / inline) ──────────────────────────────────
    const btnStyle = isHeader
        ? {
            background: 'rgba(255,255,255,0.2)',
            border: '1px solid rgba(255,255,255,0.4)',
            hoverBg: 'rgba(255,255,255,0.35)',
            iconColor: '#fff',
          }
        : {
            background: 'rgba(27,184,167,0.1)',
            border: '1px solid rgba(27,184,167,0.35)',
            hoverBg: 'rgba(27,184,167,0.2)',
            iconColor: 'var(--primary-color)',
          };

    return (
        <>
            {/* Feedback icon trigger */}
            <button
                type="button"
                onClick={handleClick}
                title="Delivery Feedback"
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 24, height: 24, borderRadius: '50%',
                    background: btnStyle.background,
                    border: btnStyle.border,
                    cursor: 'pointer', flexShrink: 0, transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = btnStyle.hoverBg)}
                onMouseLeave={(e) => (e.currentTarget.style.background = btnStyle.background)}
            >
                <i className="pi pi-question" style={{ fontSize: '0.62rem', color: btnStyle.iconColor }} />
            </button>

            {/* Desktop overlay panel */}
            {!isCompact && (
                <OverlayPanel
                    ref={opRef}
                    className="p-0"
                    style={{
                        width: 360, maxWidth: '95vw',
                        borderRadius: 16, overflow: 'hidden',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
                        border: '1px solid #e5e7eb',
                    }}
                >
                    <DeliveryFeedbackContent
                        pincode={pincode}
                        productId={productId}
                        source={source}
                        onClose={() => opRef.current?.hide()}
                    />
                </OverlayPanel>
            )}

            {/* Mobile + tablet bottom sheet */}
            {showSheet && (
                <FeedbackBottomSheet
                    pincode={pincode}
                    productId={productId}
                    source={source}
                    onClose={() => setShowSheet(false)}
                />
            )}
        </>
    );
};
